import * as React from 'react';
import {
  DotsThree,
  GitPullRequest,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  IconButton,
  Input,
  Tabs,
} from '@fragments-sdk/ui';
import { ActionRail, LeadCell } from '@/components/table-kit';
import { formatPrUpdatedAt, plural } from '../findings-display-model';
import type { IndexedPullRequest } from '../findings-types';
import {
  filterPrInboxRows,
  getPrCheckDots,
  getPrInboxCounts,
  getPrInboxStatus,
  PR_INBOX_TABS,
  type PrInboxView,
} from './pr-workbench-model';
import s from '../findings.module.scss';

export function PrPullRequestsIndex({
  rows,
  error,
  notice,
  onSelect,
  onRepository,
}: {
  rows: IndexedPullRequest[];
  error: string | null;
  notice?: string | null;
  onSelect: (prNumber: number) => void;
  onRepository: () => void;
}) {
  const [view, setView] = React.useState<PrInboxView>('open');
  const [query, setQuery] = React.useState('');
  const userSelectedView = React.useRef(false);
  const counts = React.useMemo(() => getPrInboxCounts(rows), [rows]);
  React.useEffect(() => {
    if (userSelectedView.current || counts[view] > 0) return;
    if (counts.open > 0) setView('open');
    else if (counts.merged > 0) setView('merged');
    else if (counts.closed > 0) setView('closed');
  }, [counts, view]);
  const visibleRows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return filterPrInboxRows(rows, view).filter((row) => {
      if (!q) return true;
      return (
        row.title.toLowerCase().includes(q) ||
        row.branch.toLowerCase().includes(q) ||
        String(row.prNumber).includes(q) ||
        (row.author?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [query, rows, view]);
  const columns = React.useMemo(
    () => createPrInboxColumns(onSelect),
    [onSelect],
  );

  return (
    <section className={s.prInbox} aria-label="Pull requests">
      <div className={s.prInboxHeader}>
        <div>
          <h1>Pull requests</h1>
          <p>
            Every PR is checked against your design-system rules. Select a row
            to inspect findings and apply safe fixes.
          </p>
        </div>
        <div className={s.prInboxHeaderActions}>
          <Input
            className={s.prInboxSearch}
            type="text"
            size="sm"
            placeholder="Search PRs..."
            value={query}
            onValueChange={setQuery}
            aria-label="Search pull requests"
            startAdornment={<MagnifyingGlass size={13} />}
            withFieldWrapper={false}
          />
          <Button
            type="button"
            variant="outlined"
            size="sm"
            onClick={onRepository}
          >
            Repository backlog
          </Button>
        </div>
      </div>

      {error ? (
        <div className={s.prActionStatus} data-tone="error">
          {error}
        </div>
      ) : notice ? (
        <div className={s.prActionStatus}>{notice}</div>
      ) : null}

      <Tabs
        value={view}
        onValueChange={(next) => {
          userSelectedView.current = true;
          setView(next as PrInboxView);
        }}
        variant="pills"
      >
        <Tabs.List className={s.prInboxTabs}>
          {PR_INBOX_TABS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
              <span>{counts[tab.value]}</span>
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <DataTable<IndexedPullRequest>
        aria-label="Pull requests"
        caption="Pull requests"
        captionHidden
        bordered
        density="condensed"
        size="sm"
        columns={columns}
        data={visibleRows}
        getRowId={(row) => String(row.prNumber)}
        onRowClick={(row) => onSelect(row.prNumber)}
        emptyState={
          <PrInboxEmpty
            message={
              query
                ? 'Clear search to see all pull requests.'
                : 'Repository findings are still available in the backlog.'
            }
          />
        }
        wrapperClassName={s.prInboxTableShell}
        className={s.prInboxTable}
      />
    </section>
  );
}

function createPrInboxColumns(
  onSelect: (prNumber: number) => void,
): DataTableColumn<IndexedPullRequest>[] {
  return [
    {
      id: 'pull-request',
      header: 'Pull request',
      minSize: 420,
      size: 520,
      cell: ({ row }: { row: { original: IndexedPullRequest } }) => (
        <PrPullRequestCell row={row.original} />
      ),
    },
    {
      id: 'checks',
      header: 'Checks',
      minSize: 112,
      size: 120,
      cell: ({ row }: { row: { original: IndexedPullRequest } }) => (
        <PrChecksCell row={row.original} />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      minSize: 132,
      size: 140,
      cell: ({ row }: { row: { original: IndexedPullRequest } }) => (
        <PrStatusCell row={row.original} />
      ),
    },
    {
      id: 'updated',
      header: 'Updated',
      minSize: 146,
      size: 160,
      cell: ({ row }: { row: { original: IndexedPullRequest } }) =>
        formatPrUpdatedAt(row.original.updatedAt),
    },
    {
      id: 'action',
      header: 'Action',
      align: 'right',
      minSize: 74,
      size: 82,
      enableSorting: false,
      cell: ({ row }: { row: { original: IndexedPullRequest } }) => (
        <ActionRail>
          <IconButton
            aria-label={`Open PR #${row.original.prNumber} actions`}
            size="sm"
            variant="outlined"
            onClick={() => onSelect(row.original.prNumber)}
          >
            <DotsThree size={14} weight="bold" />
          </IconButton>
        </ActionRail>
      ),
    },
  ];
}

function PrPullRequestCell({ row }: { row: IndexedPullRequest }) {
  return (
    <LeadCell
      glyph={
        <span className={s.prInboxAvatar} aria-hidden>
          {initials(row.author ?? row.title)}
        </span>
      }
      label={row.title}
      sublabel={`${row.branch} · #${row.prNumber} · ${row.author ?? 'unknown'}`}
    />
  );
}

function PrChecksCell({ row }: { row: IndexedPullRequest }) {
  const findingsLabel = row.run
    ? plural(row.run.groupCount, 'finding')
    : 'No scan yet';

  return (
    <span className={s.prCheckDots} aria-label={findingsLabel}>
      {getPrCheckDots(row).map((dot, index) => (
        <span key={`${row.prNumber}:${index}`} data-dot={dot} />
      ))}
    </span>
  );
}

function PrStatusCell({ row }: { row: IndexedPullRequest }) {
  const status = getPrInboxStatus(row);

  return (
    <Badge
      variant="label"
      size="sm"
      dot
      className={s.prGateBadge}
      data-gate={status.gate}
    >
      {status.label}
    </Badge>
  );
}

function PrInboxEmpty({ message }: { message: string }) {
  return (
    <div className={s.prInboxEmpty}>
      <GitPullRequest size={28} />
      <strong>No pull requests in this view</strong>
      <span>{message}</span>
    </div>
  );
}

function initials(value: string) {
  return value
    .split(/[\s@/_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
